import { styled } from "@mui/material/styles";
import {
  Box,
  Chip,
  DialogContent,
  IconButton,
  Typography,
} from "@mui/material";
import { colors } from "../../../theme/theme";
import { CustomBtn } from "../../../components/Common/CustomBtn";

export const StyledDialogTitle = styled(Box)(({ theme }) => ({
  paddingBottom: theme.spacing(2),
  paddingTop: theme.spacing(3),
  paddingLeft: theme.spacing(4),
  paddingRight: theme.spacing(4),
  backgroundColor: colors.headerBg,
  color: "white",
  [theme.breakpoints.down("md")]: {
    paddingBottom: theme.spacing(1),
    paddingTop: theme.spacing(2),
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
  },
  [theme.breakpoints.down("sm")]: {
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
  },
}));

export const StyledDialogTitleInner = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  width: "100%",
  gap: theme.spacing(1.5),
}));

export const StyledDialogTitleText = styled(Typography)(({ theme }) => ({
  fontFamily: "'Gotham', sans-serif",
  fontWeight: 700,
  color: colors.primary,
  width: "100%",
  display: "flex",
  marginLeft: "52px",
  justifyContent: "center",
  [theme.breakpoints.down("md")]: {
    fontSize: 22,
    fontWeight: 600,
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: 16,
    fontWeight: 500,
  },
}));

export const StyledDialogTitleCloseButton = styled(IconButton)({
  color: colors.primary,
  "&:hover": {
    backgroundColor: "rgba(255, 255, 255, 0.1)",
  },
});

export const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  paddingTop: theme.spacing(3),
  paddingLeft: theme.spacing(4),
  paddingRight: theme.spacing(4),
  paddingBottom: theme.spacing(2),
  backgroundColor: "#FAFAFA",
}));

export const StyledSectionHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  marginBottom: theme.spacing(2),
}));

export const StyledSectionTitle = styled(Typography)(({ theme }) => ({
  fontFamily: "'Gotham', sans-serif",
  fontSize: "18px",
  color: colors.primary,
  [theme.breakpoints.down("md")]: {
    fontSize: 16,
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: 14,
  },
}));

export const StyledInfoCard = styled(Box)(({ theme }) => ({
  border: `1px solid ${colors.primary}20`,
  borderRadius: "14px",
  backgroundColor: `${colors.primary}05`,
  padding: theme.spacing(3),
}));

export const StyledLabelValueRow = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  marginBottom: theme.spacing(1),
}));

export const StyledLabel = styled(Typography)({
  color: colors.primary,
  fontWeight: 700,
  textTransform: "uppercase",
  fontSize: "0.7rem",
  letterSpacing: 0.5,
  fontFamily: "'Gotham', sans-serif",
});

export const StyledValue = styled(Typography, {
  shouldForwardProp: (prop) => !["bold", "$preWrap", "$mt"].includes(prop),
})(({ theme, bold, $preWrap, $mt }) => ({
  fontWeight: bold ? 600 : 500,
  color: colors.primary,
  fontFamily: "'Gotham', sans-serif",
  marginLeft: 28,
  wordBreak: "break-word",
  ...($preWrap && { whiteSpace: "pre-wrap" }),
  ...($mt !== undefined && { marginTop: theme.spacing($mt) }),
}));

export const StyledIconWrap = styled(Box)({
  "& .MuiSvgIcon-root": {
    fontSize: 18,
    color: colors.primary,
  },
});

export const StyledSectionIconWrap = styled(Box)({
  "& .MuiSvgIcon-root": {
    color: colors.primary,
    fontSize: 22,
  },
});

export const StyledStatusLabel = styled(Typography)(({ theme }) => ({
  color: colors.primary,
  fontWeight: 700,
  textTransform: "uppercase",
  fontSize: "0.7rem",
  letterSpacing: 0.5,
  fontFamily: "'Gotham', sans-serif",
  display: "block",
  width: "auto",
  justifySelf: "center",
  marginBottom: theme.spacing(1),
  [theme.breakpoints.down("md")]: {
    fontWeight: 600,
    fontSize: "0.5rem",
  },
}));

export const StyledStatusChip = styled(Chip, {
  shouldForwardProp: (prop) =>
    !["$bgcolor", "$color", "$border"].includes(prop),
})(({ $bgcolor, $color, $border, theme }) => ({
  fontWeight: 600,
  paddingLeft: 16,
  paddingRight: 16,
  height: 32,
  marginLeft: 8,
  fontFamily: "'Gotham', sans-serif",
  ...($bgcolor && { backgroundColor: $bgcolor }),
  ...($color && { color: $color }),
  ...($border && { border: $border }),
  [theme.breakpoints.down("md")]: {
    fontSize: "0.6rem",
    paddingLeft: 10,
    paddingRight: 10,
    height: 28,
    marginLeft: 4,
  },
  [theme.breakpoints.down("sm")]: {
    fontWeight: 500,
    fontSize: "0.5rem",
    paddingLeft: 8,
    paddingRight: 8,
    height: 24,
    marginLeft: 0,
  },
}));

export const StyledFollowUpBox = styled(Box)(({ theme }) => ({
  border: `1px solid ${colors.primary}20`,
  borderRadius: "14px",
  backgroundColor: `${colors.primary}08`,
  padding: theme.spacing(3),
  textAlign: "center",
}));

export const StyledFollowUpText = styled(Typography)(({ theme }) => ({
  color: colors.primary,
  marginBottom: theme.spacing(2),
  fontFamily: "'Gotham', sans-serif",
  fontWeight: 500,
}));

export const StyledFollowUpButton = styled(CustomBtn)(({ theme }) => ({
  margin: "auto",
  [theme.breakpoints.down(850)]: {
    width: "auto",
  },
}));

export const StyledVirtualMeetingLink = styled("a")({
  color: colors.primaryText,
  textDecoration: "underline",
  fontWeight: 600,
  fontFamily: "'Gotham', sans-serif",
  marginLeft: "28px",
  display: "block",
  wordBreak: "break-all",
});

const CHARGE_PATIENT_GREEN = "#4CAF50";
const CHARGE_PATIENT_GREEN_HOVER = "#388E3C";

export const StyledChargePatientBox = styled(Box)(({ theme }) => ({
  border: `2px solid ${CHARGE_PATIENT_GREEN}`,
  borderRadius: "14px",
  backgroundColor: "rgba(76, 175, 80, 0.05)",
  padding: theme.spacing(3),
  textAlign: "center",
}));

export const StyledChargePatientText = styled(Typography)(({ theme }) => ({
  color: colors.primary,
  marginBottom: theme.spacing(2),
  fontFamily: "'Gotham', sans-serif",
  fontWeight: 500,
}));

export const StyledChargePatientButton = styled(CustomBtn)(({ theme }) => ({
  backgroundColor: CHARGE_PATIENT_GREEN,
  "&:hover": {
    backgroundColor: CHARGE_PATIENT_GREEN_HOVER,
    opacity: 0.9,
  },
  [theme.breakpoints.down(850)]: {
    width: "auto",
  },
}));
